import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  message,
  Spin,
  Table,
} from 'antd';
import {
  TrophyOutlined,
  TeamOutlined,
  BarChartOutlined,
  CalendarOutlined,
  FlagOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, getAuthHeaders } from '../api';
import type { DatabaseStats, Country, Discipline, Competition } from '../types';

const { Title } = Typography;

export interface DatabaseTabPrefetched {
  stats: DatabaseStats;
  countries: Country[];
  disciplines: Discipline[];
  competitions: Competition[];
}

interface DatabaseStatsTabProps {
  prefetched?: DatabaseTabPrefetched | null;
}

const DatabaseStatsTab: React.FC<DatabaseStatsTabProps> = ({ prefetched }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DatabaseStats | null>(prefetched?.stats ?? null);
  const [countries, setCountries] = useState<Country[]>(prefetched?.countries ?? []);
  const [disciplines, setDisciplines] = useState<Discipline[]>(prefetched?.disciplines ?? []);
  const [competitions, setCompetitions] = useState<Competition[]>(prefetched?.competitions ?? []);
  const [loading, setLoading] = useState(!prefetched);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (prefetched) {
      setStats(prefetched.stats);
      setCountries(prefetched.countries);
      setDisciplines(prefetched.disciplines);
      setCompetitions(prefetched.competitions);
      setLoading(false);
      return;
    }
    if (!user) {
      setLoading(false);
      return;
    }
    const fetchAll = async () => {
      try {
        const headers = await getAuthHeaders();
        const [statsRes, countriesRes, disciplinesRes, compsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/stats`, { headers }),
          axios.get(`${API_BASE_URL}/api/countries`, { headers }),
          axios.get(`${API_BASE_URL}/api/disciplines`, { headers }),
          axios.get(`${API_BASE_URL}/api/competitions`, { headers, params: { per_page: 100, page: 1 } }),
        ]);
        setStats(statsRes.data);
        setCountries(countriesRes.data.countries ?? []);
        setDisciplines(disciplinesRes.data.disciplines ?? []);
        setCompetitions(compsRes.data?.competitions ?? []);
      } catch (error) {
        message.error('Failed to fetch statistics data');
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [user, prefetched]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const disciplineCategories = Array.from(new Set(disciplines.map((d) => d.category)));

  return (
    <div style={{ padding: isMobile ? '0 8px' : 0 }}>
      <Title level={4} style={{ marginBottom: 16, fontSize: isMobile ? 16 : undefined }}>
        Database Statistics & Information
      </Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card size={isMobile ? 'small' : undefined}>
            <Statistic
              title={<span style={{ fontSize: isMobile ? 12 : undefined }}>Total Competitions</span>}
              value={stats?.competitions ?? 0}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: 'var(--bright-sky)', fontSize: isMobile ? 20 : undefined }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size={isMobile ? 'small' : undefined}>
            <Statistic
              title={<span style={{ fontSize: isMobile ? 12 : undefined }}>Total Athletes</span>}
              value={stats?.athletes ?? 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: 'var(--blue-bell)', fontSize: isMobile ? 20 : undefined }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size={isMobile ? 'small' : undefined}>
            <Statistic
              title={<span style={{ fontSize: isMobile ? 12 : undefined }}>Total Results</span>}
              value={stats?.results ?? 0}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: 'var(--black)', fontSize: isMobile ? 20 : undefined }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size={isMobile ? 'small' : undefined}>
            <Statistic
              title={<span style={{ fontSize: isMobile ? 12 : undefined }}>Total Events</span>}
              value={stats?.events ?? 0}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: 'var(--racing-red)', fontSize: isMobile ? 20 : undefined }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ alignItems: 'stretch' }}>
        <Col xs={24} lg={12}>
          <Card title="Countries" extra={<FlagOutlined />} size={isMobile ? 'small' : undefined} style={{ height: '100%', display: 'flex', flexDirection: 'column' }} styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 360 } }}>
            <div style={{ flex: 1, maxHeight: 320, overflowY: 'auto' }}>
              {countries.map((c, i) => (
                <div
                  key={c.code}
                  style={{
                    padding: isMobile ? '6px 0' : '8px 0',
                    borderBottom: i < countries.length - 1 ? '1px solid var(--pale-sky)' : 'none',
                    fontSize: isMobile ? 12 : undefined,
                  }}
                >
                  <strong>{c.name}</strong> ({c.code})
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, textAlign: 'center', color: 'var(--black)', fontSize: isMobile ? 12 : undefined }}>
              Total: {countries.length} countries
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Disciplines" extra={<BarChartOutlined />} size={isMobile ? 'small' : undefined} style={{ height: '100%', display: 'flex', flexDirection: 'column' }} styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 360 } }}>
            <div style={{ flex: 1, maxHeight: 320, overflowY: 'auto' }}>
              {disciplineCategories.map((cat, i) => {
                const list = disciplines.filter((d) => d.category === cat);
                return (
                  <div
                    key={cat}
                    style={{
                      padding: isMobile ? '8px 0' : '12px 0',
                      borderBottom: i < disciplineCategories.length - 1 ? '1px solid var(--pale-sky)' : 'none',
                    }}
                  >
                    <span
                      style={{
                        backgroundColor: 'var(--pale-sky)',
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontSize: isMobile ? 11 : 12,
                        marginRight: 8,
                        display: 'inline-block',
                        marginBottom: 6,
                      }}
                    >
                      {cat} ({list.length})
                    </span>
                    <ul style={{ margin: 0, paddingLeft: 20, fontSize: isMobile ? 12 : 13 }}>
                      {list.map((d) => (
                        <li key={d.discipline_code} style={{ marginBottom: 2 }}>
                          <strong>{d.discipline_name}</strong>
                          <span style={{ color: 'var(--black)', marginLeft: 6 }}>{d.discipline_code}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 12, textAlign: 'center', color: 'var(--black)', fontSize: isMobile ? 12 : undefined }}>
              Total: {disciplines.length} disciplines
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title="Competitions" extra={<TrophyOutlined />} size={isMobile ? 'small' : undefined}>
            <Table
              dataSource={competitions}
              rowKey="id"
              size={isMobile ? 'small' : 'middle'}
              pagination={{
                pageSize: 15,
                showSizeChanger: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} competitions`,
                size: isMobile ? 'small' : undefined,
              }}
              scroll={{ x: isMobile ? 500 : undefined }}
              columns={[
                {
                  title: 'Competition',
                  dataIndex: 'name',
                  key: 'name',
                  ellipsis: true,
                  render: (name: string, row: Competition) => (
                    <span title={name}>
                      <strong>{name}</strong>
                      {row.venue ? ` · ${row.venue}` : ''}
                    </span>
                  ),
                },
                {
                  title: 'Results',
                  dataIndex: 'result_count',
                  key: 'result_count',
                  width: 90,
                  align: 'right',
                  render: (n: number) => n ?? 0,
                },
                {
                  title: 'Athletes',
                  dataIndex: 'athlete_count',
                  key: 'athlete_count',
                  width: 90,
                  align: 'right',
                  render: (n: number) => n ?? 0,
                },
                {
                  title: 'Date range',
                  key: 'date_range',
                  width: isMobile ? 120 : 180,
                  render: (_: unknown, row: Competition) => {
                    const start = row.start_date ? new Date(row.start_date).toLocaleDateString() : '—';
                    const end = row.end_date && row.end_date !== row.start_date ? new Date(row.end_date).toLocaleDateString() : null;
                    return end ? `${start} - ${end}` : start;
                  },
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title="Database Information" size={isMobile ? 'small' : undefined}>
            <div style={{ padding: isMobile ? '12px 0' : '20px 0', fontSize: isMobile ? 12 : undefined }}>
              <p><strong>Data Source:</strong> This database contains comprehensive athletics data including competitions, athletes, events, and results from around the world.</p>
              <p><strong>Coverage:</strong> The database spans multiple countries and includes various athletics disciplines such as track and field events.</p>
              <p><strong>Data Quality:</strong> Results include detailed performance metrics, wind conditions (where applicable), and qualification status.</p>
              <p><strong>Updates:</strong> Data is regularly synchronized and maintained to ensure accuracy and completeness.</p>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DatabaseStatsTab;
